"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import writeXlsxFile from "write-excel-file";

import { RequiredBuyPremiumButton } from "@/app/_components/required/buy-premium-button";
import { Spinner } from "@/components/ui/spinner";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";

import { formatBRL } from "@/helper/currency";
import { formatDateAuction } from "@/helper/date";
import { getAppUrl } from "@/helper/url";

import { uf } from "@/schemas/house";

import { usePostDownloadProperties } from "@/queries/mutations/use-post-properties-download";

import states from "@/static/states.json";

import type { LeilaoSQLFull } from "@/types";

const xlsxSchema = [
  {
    column: "UF",
    type: String,
    value: (property: LeilaoSQLFull) => property.uf?.toUpperCase(),
    width: 10,
    align: "center",
    fontWeight: "bold",
    borderColor: "#000000",
  },
  {
    column: "Cidade",
    type: String,
    value: (property: LeilaoSQLFull) => property.cidade_descricao,
    width: 30,
    align: "left",
    fontWeight: "bold",
    borderColor: "#000000",
  },
  {
    column: "Endereço",
    type: String,
    value: (property: LeilaoSQLFull) =>
      `${property.endereco}, ${property.bairro}`,
    width: 60,
    align: "left",
    fontWeight: "bold",
    borderColor: "#000000",
  },
  {
    column: "Preço (1° leilão)",
    type: String,
    value: (property: LeilaoSQLFull) => formatBRL(property.preco1),
    width: 20,
    align: "right",
    fontWeight: "bold",
    borderColor: "#000000",
  },
  {
    column: "Preço (2° leilão)",
    type: String,
    value: (property: LeilaoSQLFull) =>
      property.preco2 ? formatBRL(property.preco2) : "",
    width: 20,
    align: "right",
    fontWeight: "bold",
    borderColor: "#000000",
  },
  {
    column: "Desconto",
    type: String,
    value: (property: LeilaoSQLFull) =>
      property.desconto ? `${property.desconto}%` : "",
    width: 10,
    align: "center",
    fontWeight: "bold",
    borderColor: "#000000",
  },
  {
    column: "Valor de avaliação",
    type: String,
    value: (property: LeilaoSQLFull) =>
      property.valor_de_avaliacao ? formatBRL(property.valor_de_avaliacao) : "",
    width: 20,
    align: "right",
    fontWeight: "bold",
    borderColor: "#000000",
  },
  {
    column: "Modalidade de venda",
    type: String,
    value: (property: LeilaoSQLFull) => property.modalidade_de_venda,
    width: 20,
    align: "left",
    fontWeight: "bold",
    borderColor: "#000000",
  },
  {
    column: "Tipo",
    type: String,
    value: (property: LeilaoSQLFull) => property.tipo,
    width: 15,
    align: "left",
    fontWeight: "bold",
    borderColor: "#000000",
  },
  {
    column: "Descrição",
    type: String,
    value: (property: LeilaoSQLFull) => property.descricao,
    width: 60,
    align: "left",
    fontWeight: "bold",
    borderColor: "#000000",
  },
  {
    column: "Area",
    type: Number,
    format: "#,##0.00",
    value: (property: LeilaoSQLFull) => property.total_area,
    width: 10,
    align: "right",
    fontWeight: "bold",
    borderColor: "#000000",
  },
  {
    column: "Area privativa",
    type: Number,
    format: "#,##0.00",
    value: (property: LeilaoSQLFull) => property.total_area_privativa,
    width: 20,
    align: "right",
    fontWeight: "bold",
    borderColor: "#000000",
  },
  {
    column: "Area terreno",
    type: Number,
    format: "#,##0.00",
    value: (property: LeilaoSQLFull) => property.total_area_terreno,
    width: 10,
    align: "right",
    fontWeight: "bold",
    borderColor: "#000000",
  },
  {
    column: "Data de licitação (1° leilão)",
    type: String,
    value: (property: LeilaoSQLFull) =>
      property.data_de_licitacao1
        ? formatDateAuction(property.data_de_licitacao1)
        : null,
    width: 15,
    align: "center",
    fontWeight: "bold",
    borderColor: "#000000",
  },
  {
    column: "Data de licitação (2 leilão)",
    type: String,
    value: (property: LeilaoSQLFull) =>
      property.data_de_licitacao2
        ? formatDateAuction(property.data_de_licitacao2)
        : null,
    width: 15,
    align: "center",
    fontWeight: "bold",
    borderColor: "#000000",
  },
  {
    column: "Inscrição imóbiliaria",
    type: String,
    value: (property: LeilaoSQLFull) => property.inscricao_imobiliaria,
    width: 20,
    align: "left",
    fontWeight: "bold",
    borderColor: "#000000",
  },
  {
    column: "Matrícula",
    type: String,
    value: (property: LeilaoSQLFull) => property.matricula,
    width: 10,
    align: "center",
    fontWeight: "bold",
    borderColor: "#000000",
  },
  {
    column: "Comarca",
    type: String,
    value: (property: LeilaoSQLFull) => property.comarca,
    width: 30,
    align: "left",
    fontWeight: "bold",
    borderColor: "#000000",
  },
  {
    column: "Link",
    type: String,
    value: (property: LeilaoSQLFull) =>
      `${getAppUrl()}/leilao-de-imoveis/${property.uf}/imovel/${property.numero_do_leilao}`,
    width: 80,
    align: "left",
    fontWeight: "bold",
    borderColor: "#000000",
  },
];
const formSchema = z.object({ uf });

interface Props {
  enabled: boolean;
  premium?: boolean;
  uf?: z.infer<typeof uf>;
}
export const Spreadsheets = ({ enabled, premium, uf }: Props) => {
  const { mutate, isPending } = usePostDownloadProperties();

  const schema = formSchema.extend({
    email: premium
      ? z
          .string({ required_error: "Informe o seu melhor email" })
          .email("Email inválido")
          .trim()
          .optional()
      : z
          .string({ required_error: "Informe o seu melhor email" })
          .email("Email inválido")
          .trim(),
  });

  const form = useForm<z.infer<typeof schema>>({
    defaultValues: {
      uf,
    },
    resolver: zodResolver(schema),
  });

  const onSubmit = async (data: z.infer<typeof schema>) => {
    if (!enabled || isPending) return null;

    const { uf, email } = schema.parse(data);

    mutate(
      { uf, email },
      {
        onSuccess: async (result) => {
          await writeXlsxFile(result, {
            // @ts-ignore
            schema: xlsxSchema as any,
            fileName: `pro-leilao-planilha-de-imoveis-${uf}.xlsx`,
            fontFamily: "Arial",
            fontSize: 12,
            sheet: `ProLeilao.com.br | Imóveis ${uf}`,
            rowHeight: 25,
            stickyRow: 1,
            defaultAlignment: "left",
            headerStyle: {
              backgroundColor: "#1a73e8",
              color: "#ffffff",
              fontWeight: "bold",
              align: "center",
            },
          });
        },
      }
    );
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="w-full space-y-6">
        <FormField
          control={form.control}
          name="uf"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Estado</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o estado do imóvel" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {states
                    .sort((a, b) => a.pib - b.pib)
                    .map((item) => (
                      <SelectItem key={item.value} value={item.value}>
                        {item.label} -{" "}
                        <span className="uppercase">{item.value}</span>
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
        {!premium ? (
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Digite o seu melhor email</FormLabel>
                <Input
                  placeholder="maria@email.com"
                  type="email"
                  onChange={field.onChange}
                  defaultValue={field.value}
                />
                <FormMessage />
              </FormItem>
            )}
          />
        ) : null}
        <div className="flex flex-row gap-2">
          <Button
            type="reset"
            variant="secondary"
            className="w-full"
            onClick={() => {
              form.reset();
            }}
          >
            Limpar
          </Button>
          <RequiredBuyPremiumButton enabled={enabled}>
            <Button type="submit" className="w-full" disabled={isPending}>
              {isPending ? <Spinner inverse /> : "Baixar planilha"}
            </Button>
          </RequiredBuyPremiumButton>
        </div>
      </form>
    </Form>
  );
};
